<template>
    <widget-card
        :icon="QrCode"
        :info-tooltip="trans('dashboard.v2.mean_downtime.info')"
        :loading="loading"
        :title="trans('dashboard.v2.mean_downtime.title')"
    >
        <template v-if="lastUpdated" #header-subtitle>
            <div class="small mt-1 text-muted">{{ trans('dashboard.v2.last_update') }}: {{ lastUpdated }}</div>
        </template>
        <template #header-end>
            <div class="d-flex align-items-center gap-2">
                <jet-button v-if="debug" class="btn-icon" variant="secondary" @click="showDebugInfo = !showDebugInfo">
                    <icon name="cil-bug" />
                </jet-button>
            </div>
        </template>
        <div class="h-100">
            <div class="d-flex flex-column flex-md-row align-items-stretch h-100">
                <chart-loading-skeleton v-if="loading" />
                <empty-widget-state v-else-if="!hasData" :text="trans('dashboard.v2.mean_downtime.empty_state')" />
                <template v-else>
                    <c-chart
                        :key="componentKey"
                        :custom-tooltips="false"
                        :data="chartData"
                        :options="chartOptions"
                        :plugins="[ChartDataLabels]"
                        class="grow"
                        type="bar"
                    />
                    <chart-legend :legend-items="legendItems" :loading="loading" />
                </template>
            </div>
        </div>
    </widget-card>
    <debug-view v-if="debug" v-model="showDebugInfo" title="Mean Down Time<br/><small>Debug Info</small>">
        <pre style="background-color: #fefefe">{{ debugInfo }}</pre>
    </debug-view>
</template>

<script setup>
import { computed, toRef } from 'vue';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import ChartLoadingSkeleton from '@/Pages/DashboardV2/Components/ChartLoadingSkeleton.vue';
import ChartLegend from '@/Pages/DashboardV2/Components/ChartLegend.vue';
import EmptyWidgetState from '@/Pages/DashboardV2/Components/EmptyWidgetState.vue';
import { CChart } from '@coreui/vue-chartjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { trans } from '@/mixins/i18n';
import QrCode from '../../../../icons/QrCode.vue';
import DebugView from '@/Components/DebugView.vue';
import JetButton from '@/Jetstream/Button.vue';
import { useChartWidget } from '@/composables/useChartWidget';
import { getBaseChartOptions } from '@/Utils/chartDefaults';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
    availableGroupings: {
        type: Array,
        default: () => [],
    },
});

const { data, loading, componentKey, hasData, lastUpdated, selectedGrouping, groupingOptions, debug, showDebugInfo, debugInfo } = useChartWidget({
    dataUrl: computed(() => props.widgetData?.dataUrl),
    filters: toRef(props, 'filters'),
    groupingOptions: computed(() => props.widgetData?.groupingOptions?.data ?? []),
    availableGroupings: toRef(props, 'availableGroupings'),
    parseResponse: (response) => ({
        data: response.data.data,
        lastUpdated: response.data.lastUpdated,
        debug: response.data.debug,
    }),
    defaultGrouping: 'type',
});

const legendItems = computed(() => [
    {
        key: 'sla',
        text: trans('dashboard.v2.mean_downtime.legend.sla'),
        color: '#2c54ea',
        shape: 'square',
    },
    {
        key: 'actual',
        text: trans('dashboard.v2.mean_downtime.legend.actual'),
        color: '#9aaef9',
        shape: 'square',
    },
]);

// Extract data from consolidated format
const labels = computed(() => data.value?.map((item) => item.label) || []);
const meanDowntime = computed(() => data.value?.map((item) => item.meanDowntimeInSeconds) || []);
const actualMeanDowntime = computed(() => data.value?.map((item) => item.actualDowntimeInSeconds) || []);

// Convert seconds to decimal hours for chart calculations
const meanDowntimeHours = computed(() => meanDowntime.value.map((s) => s / 3600));

const actualMeanDowntimeHours = computed(() => actualMeanDowntime.value.map((s) => s / 3600));

const chartData = computed(() => {
    if (!hasData.value) {
        return {
            labels: [],
            datasets: [],
        };
    }

    const topValue = Math.ceil(
        Math.max(
            ...(meanDowntimeHours.value.length ? meanDowntimeHours.value : [0]),
            ...(actualMeanDowntimeHours.value.length ? actualMeanDowntimeHours.value : [0]),
        ) + 2,
    );

    return {
        labels: data.value.map((l) => l.assetType),
        datasets: [
            // SLA bars
            {
                type: 'bar',
                label: trans('dashboard.v2.mean_downtime.legend.sla'),
                order: 0,
                backgroundColor: '#2C54EA',
                borderRadius: 4,
                data: meanDowntimeHours.value,
                barPercentage: 0.4,
                // Disable labels for the actual bars
                datalabels: {
                    display: false,
                },
            },
            // Actual bars
            {
                type: 'bar',
                label: trans('dashboard.v2.mean_downtime.legend.actual'),
                order: 1,
                backgroundColor: '#9AAEF9',
                borderRadius: 4,
                data: actualMeanDowntimeHours.value,
                barPercentage: 0.4,
                // Disable labels for the actual bars
                datalabels: {
                    display: false,
                },
            },
            // Transparent scatter dataset to host the datalabels above each column
            {
                type: 'scatter',
                label: 'labels',
                order: 2,
                data: labels.value.map((_, idx) => ({ x: idx, y: topValue })),
                pointRadius: 0,
                pointHoverRadius: 0,
                datalabels: {
                    display: (context) => {
                        return actualMeanDowntime.value[context.dataIndex] > 60 ? 'auto' : false;
                    },
                    direction: 'horizontal',
                    clamp: true,
                    anchor: 'end', // position relative to the point (chart top)
                    align: 'end',
                    offset: 4,
                    backgroundColor: '#9AAEF9',
                    borderRadius: 2,
                    color: '#2C54EA',
                    font: {
                        size: 10,
                        weight: 'bold',
                    },
                    formatter: (value, ctx) => {
                        const rawSeconds = actualMeanDowntime.value[ctx.dataIndex];
                        const rawHours = rawSeconds / 3600;
                        const hours = Math.floor(rawHours);
                        const minutes = Math.round((rawHours - hours) * 60)
                            .toString()
                            .padStart(2, '0');
                        return `${hours}:${minutes}h`;
                    },
                    padding: {
                        top: 2,
                        bottom: 2,
                        left: 4,
                        right: 4,
                    },
                },
            },
        ],
    };
});

const chartOptions = computed(() => {
    const maxValue = Math.max(
        ...(meanDowntimeHours.value.length ? meanDowntimeHours.value : [10]),
        ...(actualMeanDowntimeHours.value.length ? actualMeanDowntimeHours.value : [0]),
    );
    return {
        ...getBaseChartOptions(),
        animation: false,
        plugins: {
            ...getBaseChartOptions().plugins,
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (context) => labels.value[context[0].dataIndex],
                    label: (context) => {
                        // Only show our custom labels once for the first dataset
                        // as index == 2 would be the scatter point that enable the display of the value on top of the bar
                        if (context.datasetIndex <= 1) {
                            const raw = context.parsed.y;
                            const hrs = Math.floor(raw);
                            const mins = Math.round((raw - hrs) * 60)
                                .toString()
                                .padStart(2, '0');
                            return `${hrs}:${mins}h`;
                        }
                        return null;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false,
                },
                ticks: {
                    autoSkip: true,
                    maxRotation: 45,
                    minRotation: 0,
                    font: {
                        size: 11,
                    },
                },
            },
            y: {
                beginAtZero: true,
                max: Math.ceil(maxValue + 2),
                grace: '5%',
                grid: {
                    drawBorder: false,
                },
                ticks: {
                    callback: (value) => `${value}h`,
                },
            },
        },
        layout: {
            padding: {
                top: 40,
            },
        },
    };
});
</script>
